import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { t } from "../lib/i18n";
import { toast } from "sonner";
import { Lightning } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t.passwordMin);
      return;
    }
    if (password !== confirm) {
      toast.error(t.passwordsDontMatch);
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t.resetSuccess);
      setTimeout(() => nav("/login", { replace: true }), 600);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || t.resetInvalidToken;
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-white/60 mb-4" data-testid="reset-missing-token">
            {t.resetInvalidToken}
          </p>
          <Link
            to="/forgot-password"
            className="text-[#00E5FF] hover:underline text-xs uppercase tracking-[0.25em] font-bold"
          >
            {t.forgotPassword}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Lightning size={32} weight="fill" className="text-[#FF0055] mx-auto" />
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono mt-3">
            // BEYX.PL
          </div>
          <h1 className="font-display font-black uppercase italic text-5xl mt-2">
            {t.resetTitle}
          </h1>
          <p className="text-white/50 text-sm mt-2">{t.resetSub}</p>
        </div>

        <form
          onSubmit={submit}
          data-testid="reset-form"
          className="border border-white/10 bg-[#0C0C14] p-6 space-y-4"
        >
          <Field
            label={t.newPassword}
            value={password}
            onChange={setPassword}
            testid="reset-password"
          />
          <Field
            label={t.confirmPassword}
            value={confirm}
            onChange={setConfirm}
            testid="reset-confirm"
          />
          <p className="text-[10px] font-mono text-white/40">{t.passwordMin}</p>
          <button
            type="submit"
            disabled={busy}
            data-testid="reset-submit"
            className="w-full px-6 py-3 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-sm transition-colors disabled:opacity-50"
          >
            {busy ? t.sending : t.resetCta}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-2">
        {label}
      </label>
      <input
        type="password"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-[#00E5FF]"
      />
    </div>
  );
}
